import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';

import { VariableDataBinding } from 'editor/src/store/design/types';
import sameArrayResult from 'editor/src/store/design/util/sameArrayResult';
import { RootState } from 'editor/src/store/index';

import { collectVariableDataBindings, countCharacters, getVariableDataColumnValue } from 'editor/src/lib/variableData';

export interface VariableDataBoundLayer {
  uuid: number;
  name: string;
  column: string;
  /** Set, with `sampleLength`, only on a text whose character limit the sample
   *  already exceeds: every row shaped like it is refused, at checkout and again
   *  at order import. */
  maxCharCount?: number;
  sampleLength?: number;
}

export interface VariableDataSummary {
  binding: VariableDataBinding | undefined;
  /** Every layer reading a column, once per column it reads. */
  boundLayers: VariableDataBoundLayer[];
}

const sameLayers = sameArrayResult<VariableDataBoundLayer>(shallowEqual);

// See sameArrayResult: the design moves on every keystroke, and without this
// the section would re-render, and walk the design again, on each one. The
// `boundLayers` guard answers false for reselect's throwaway `{}`.
function sameSummary(a: VariableDataSummary, b: VariableDataSummary): boolean {
  return Array.isArray(a?.boundLayers) && a.binding === b.binding && sameLayers(a.boundLayers, b.boundLayers);
}

/** What the design's variable data section states: the binding, and every layer drawing on it. */
const getVariableDataSummary = createSelector(
  [(state: RootState) => state.design.designData],
  (designData): VariableDataSummary => {
    const names = new Map<number, string>();
    designData?.spreads.forEach((spread) =>
      spread.pages.forEach((page) => page.groups.media?.forEach((element) => names.set(element.uuid, element.name))),
    );

    const binding = designData?.variableData;
    const { texts, qrFields } = collectVariableDataBindings(designData);
    const seen = new Set<string>();
    const boundLayers: VariableDataBoundLayer[] = [];
    const add = (layer: VariableDataBoundLayer) => {
      const key = `${layer.uuid}:${layer.column}`;
      if (!seen.has(key)) {
        seen.add(key);
        boundLayers.push(layer);
      }
    };

    texts.forEach(({ uuid, column, maxCharCount }) => {
      const sample = getVariableDataColumnValue(binding, column);
      const sampleLength = sample === undefined ? 0 : countCharacters(sample);
      const capTooShort = maxCharCount !== undefined && sampleLength > maxCharCount;
      add({
        uuid,
        name: names.get(uuid) ?? '',
        column,
        ...(capTooShort ? { maxCharCount, sampleLength } : {}),
      });
    });
    qrFields.forEach(({ uuid, column }) => add({ uuid, name: names.get(uuid) ?? '', column }));

    return { binding, boundLayers };
  },
  { memoizeOptions: { resultEqualityCheck: sameSummary } },
);

export default getVariableDataSummary;
